/**
 * Display & UI Action Definitions
 *
 * Actions for switching views, toggling interaction highlights,
 * opening panels, and other UI operations. These are registered in
 * the Gibbs action registry so the LLM can select them when a user
 * asks for display changes in natural language — "switch to ligand
 * view", "open the compound table", "show hydrogen bonds", etc.
 *
 * Unlike computational actions (dock, minimize, score), these do NOT
 * produce outputs for downstream workflow steps. They change the
 * visual state of BMaps. They should generally NOT be included in
 * multi-step workflow plans — they're single-action commands.
 *
 * @module gibbs/registry/actions/display
 */

import { param } from '../schema';

// ─── View Switching ────────────────────────────────────────────────────────

export const setView = {
    id: 'set-view',
    name: 'Switch Camera Zoom Level',
    category: 'Display',
    description: 'Change the CAMERA ZOOM LEVEL — NOT the rendering style. "protein" zooms out to show the full protein. "ligand" zooms into the binding site. "publication" is a clean presentation view. To change how atoms LOOK (sticks, cartoon, surface, etc.), use set-display-style instead.',
    inputs: [
        param('view', 'string', 'Camera zoom level', true, { options: ['ligand', 'protein', 'publication'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetView',
    serverCommand: null,
    jobType: null,
};

// ─── Interaction Highlights ────────────────────────────────────────────────

export const showHydrogenBonds = {
    id: 'show-hydrogen-bonds',
    name: 'Show Hydrogen Bonds',
    category: 'Display',
    description: 'Toggle hydrogen bond visualization in the 3D viewer. Shows dashed lines between H-bond donors and acceptors. Use "normal" for standard H-bonds or "includeWeak" for all including weak interactions.',
    inputs: [
        param('mode', 'string', 'Display mode', true, { defaultValue: 'normal', options: ['normal', 'includeWeak', 'none'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetHBonds',
    serverCommand: null,
    jobType: null,
};

export const showPiPiStacking = {
    id: 'show-pi-pi-stacking',
    name: 'Show Pi-Pi Stacking',
    category: 'Display',
    description: 'Toggle pi-pi stacking interaction visualization in the 3D viewer.',
    inputs: [
        param('show', 'boolean', 'Show or hide', true, { defaultValue: true }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetPiPiStacking',
    serverCommand: null,
    jobType: null,
};

export const showLigandSolvation = {
    id: 'show-ligand-solvation',
    name: 'Show Ligand Solvation',
    category: 'Display',
    description: 'Toggle ligand functional group desolvation highlighting in the 3D viewer. Highlights regions of the ligand where desolvation costs or gains are significant.',
    inputs: [
        param('show', 'boolean', 'Show or hide', true, { defaultValue: true }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetLigandSolvation',
    serverCommand: null,
    jobType: null,
};

// ─── Inspector Panels ──────────────────────────────────────────────────────

export const showInspectorTab = {
    id: 'show-inspector-tab',
    name: 'Open Inspector Tab',
    category: 'Display',
    description: 'Open a specific tab in the Inspector panel. The energies tab shows per-compound energy breakdown; the properties tab shows molecular properties; the structure tab shows structural information.',
    inputs: [
        param('tab', 'string', 'Inspector tab to open', true, { options: ['energies', 'properties', 'structure', 'interactionDiagram', 'GiFE'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ShowInspectorTab',
    serverCommand: null,
    jobType: null,
};

// ─── Compound Table ────────────────────────────────────────────────────────

export const openCompoundTable = {
    id: 'open-compound-table',
    name: 'Open Compound Table',
    category: 'Display',
    description: 'Open the Compound Table side panel showing all loaded compounds with their structures, energies, properties, and other data in a sortable table. Has tabs for Structure, Energies, Properties, LID, GIFE, and Services.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenCompoundTable',
    serverCommand: null,
    jobType: null,
};

// NOTE: rotate-view and spin-view were removed because 3Dmol.js
// viewer.rotate() is broken in this build — confirmed by direct
// console call. Causes protein to disappear and page to freeze.
// Mouse-drag rotation works fine (different internal mechanism).
// If/when 3Dmol.js is updated/fixed, re-add these actions.

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    setView,
    showHydrogenBonds,
    showPiPiStacking,
    showLigandSolvation,
    showInspectorTab,
    openCompoundTable,
];
